import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FAQCategory from "@/components/FAQCategory";
import FAQFilter from "@/components/FAQFilter";
import { useState, useMemo } from "react";
import { faqItems, faqCategories } from "@/data/faqData";
import SEO, { createFAQSchema } from "@/components/SEO";

const FAQ = () => {
  // Generate FAQ schema for SEO rich snippets
  const faqSchemaItems = faqItems.slice(0, 10).map(item => ({
    question: item.question,
    answer: item.answer
  }));
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const filteredAndGroupedFAQs = useMemo(() => {
    let filtered = faqItems;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Group by category
    const grouped = faqCategories.reduce((acc, category) => {
      const categoryItems = filtered.filter(item => item.category === category.id);
      if (categoryItems.length > 0) {
        acc[category.id] = {
          ...category,
          items: categoryItems
        };
      }
      return acc;
    }, {} as Record<string, { id: string; name: string; description: string; items: typeof faqItems }>);

    return grouped;
  }, [searchTerm, selectedCategory]);

  const totalResults = Object.values(filteredAndGroupedFAQs).reduce(
    (total, category) => total + category.items.length, 
    0
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SEO
        title="AI Image Generator FAQ | Common Questions Answered"
        description="Find answers to frequently asked questions about our AI bulk image generator. Learn about FLUX models, pricing, image quality, batch generation, and more."
        keywords="ai image generator faq, bulk image generation questions, flux ai help, image generation support, ai art faq, text to image faq"
        url="/help/faq"
        jsonLd={createFAQSchema(faqSchemaItems)}
      />
      <Header />
      
      <main className="pt-24">
        <div className="container mx-auto px-6 py-8">
          <div className="text-center mb-8">
            <Badge className="mb-3 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-300 border-blue-500/30">
              FAQ
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Frequently Asked <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Questions</span>
            </h1>
            <p className="text-lg text-gray-300 mb-6 max-w-3xl mx-auto">
              Find answers to common questions about our AI image generation platform
            </p>
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search FAQ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-gray-400 text-lg py-4"
              />
            </div>
          </div>

          <div className="max-w-5xl mx-auto">
            <FAQFilter
              categories={faqCategories}
              selectedCategory={selectedCategory}
              onCategorySelect={setSelectedCategory}
            />

            {searchTerm && (
              <div className="mb-6">
                <p className="text-gray-300">
                  {totalResults} result{totalResults !== 1 ? 's' : ''} found for "{searchTerm}"
                </p>
              </div>
            )}

            {totalResults === 0 && (searchTerm || selectedCategory) && (
              <div className="text-center py-12">
                <p className="text-gray-400 text-lg mb-4">
                  No FAQs found matching your criteria.
                </p>
                <button 
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCategory(null);
                  }}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Clear filters
                </button>
              </div>
            )}
            
            {Object.values(filteredAndGroupedFAQs).map((category) => (
              <FAQCategory
                key={category.id}
                items={category.items}
                categoryName={category.name}
                categoryDescription={category.description}
              />
            ))}
          </div>

          {!searchTerm && !selectedCategory && (
            <div className="text-center mt-12">
              <p className="text-gray-400 mb-4">Still have questions?</p>
              <a 
                href="/contact" 
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
                aria-label="Contact support team for help"
              >
                Contact Support
              </a>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
